import { useEffect, useRef, useState } from 'react';
import { autoCheckIsOn } from '../utils/authSession';

const useSessionTimeout = (onLogout) => {
  // Configuration (in milliseconds)
  const INACTIVITY_TIMEOUT = 5 * 60 * 60 * 1000; // 5 hours
  const TAB_HIDDEN_TIMEOUT = 5 * 60 * 60 * 1000; // 5 hours
  const WARNING_SHOW_TIME = 60 * 1000; // Show warning 60 seconds before logout

  // State
  const [showWarning, setShowWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(0);

  // Refs to persist across re-renders
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const tabHiddenTimerRef = useRef(null);
  const lastActivityTimeRef = useRef(Date.now());
  const isTabVisibleRef = useRef(true);

  // Function to reset inactivity timer
  const resetInactivityTimer = () => {
    lastActivityTimeRef.current = Date.now();
    setShowWarning(false);
    setWarningCountdown(0);

    // Clear existing timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    // Set warning timer (15 - 0.5 minutes = 14.5 minutes)
    warningTimerRef.current = setTimeout(() => {
      if (autoCheckIsOn()) {
        resetInactivityTimer();
        return;
      }
      if (isTabVisibleRef.current) {
        setShowWarning(true);
        setWarningCountdown(WARNING_SHOW_TIME / 1000);

        // Start countdown
        let countdown = WARNING_SHOW_TIME / 1000;
        countdownTimerRef.current = setInterval(() => {
          countdown--;
          setWarningCountdown(countdown);
        }, 1000);

        // Auto logout after warning period
        inactivityTimerRef.current = setTimeout(() => {
          handleSessionTimeout();
        }, WARNING_SHOW_TIME);
      }
    }, INACTIVITY_TIMEOUT - WARNING_SHOW_TIME);
  };

  // Function to handle session timeout. An unattended auto-check must not
  // be logged out for a quiet mouse; the token is renewed separately.
  const handleSessionTimeout = () => {
    if (autoCheckIsOn()) {
      resetInactivityTimer();
      return;
    }
    // Clear all timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (tabHiddenTimerRef.current) clearTimeout(tabHiddenTimerRef.current);

    // Call logout handler
    onLogout();
  };

  // Function to extend session (when user clicks "Continue")
  const extendSession = () => {
    resetInactivityTimer();
  };

  // Listen for activity events
  useEffect(() => {
    const activityEvents = ['click', 'keypress', 'scroll', 'mousemove', 'touchstart'];

    const handleActivity = () => {
      // Only reset if tab is visible
      if (isTabVisibleRef.current) {
        resetInactivityTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Initialize timer on mount
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (tabHiddenTimerRef.current) clearTimeout(tabHiddenTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isTabVisibleRef.current = isVisible;

      if (!isVisible) {
        if (autoCheckIsOn()) return;
        // Tab is hidden - start 2-hour countdown
        console.log('[Session] Tab hidden - 2 hour logout countdown started');
        tabHiddenTimerRef.current = setTimeout(() => {
          console.log('[Session] Tab was hidden for 2 hours - logging out');
          handleSessionTimeout();
        }, TAB_HIDDEN_TIMEOUT);
      } else {
        // Tab is visible again - clear the countdown
        if (tabHiddenTimerRef.current) {
          clearTimeout(tabHiddenTimerRef.current);
          console.log('[Session] Tab visible again - countdown cleared');
        }
        // Reset inactivity timer when returning to tab
        resetInactivityTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (tabHiddenTimerRef.current) clearTimeout(tabHiddenTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    showWarning,
    warningCountdown,
    extendSession,
    handleSessionTimeout
  };
};

export default useSessionTimeout;
